import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type FaqAnswerType = 'answer' | 'unorderedList' | 'orderedList';

export type FaqQuestion = {
  question: string;
  answer: string | Array<string>;
  answerType?: FaqAnswerType;
  listHeader?: string;
};

const NUMBER_WORDS: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
};

const numberAsWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

const asCurrency = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

type CondensedAmenitiesShape = {
  pets?: string | null;
  laundry?: string | null;
  parking?: boolean | null;
};

type ModelPricingShape = {
  numBeds?: number;
  quantity?: number;
  lowPrice?: number;
};

type DistrictShape = {
  fullName?: string;
};

/**
 * Builds the FAQ question set for an active HDP listing.
 *
 * Returns an empty array when the listing has too little data to populate
 * the schema-template defaults — callers should treat that as "no FAQ".
 */
export const getHdpFaqQuestions = (listing: PropertyDetailListing | null | undefined): Array<FaqQuestion> => {
  if (!listing || listing.active === false) return [];

  const isHouse = listing.propertyType === 'house';
  const buildingName = listing.displayName;

  const condensed = (listing.amenities as unknown as { condensedAmenities?: CondensedAmenitiesShape } | undefined)
    ?.condensedAmenities;
  const petPolicy = condensed?.pets ?? null;
  const formattedPet = petPolicy ? petPolicy.toLowerCase() : null;
  const laundry = condensed?.laundry ?? null;
  const formattedLaundry = laundry ? laundry.toLowerCase() : null;
  const parking = condensed?.parking ?? null;

  const city = listing.address?.city;
  const neighborhood = (listing.address as { neighborhood?: string } | undefined)?.neighborhood;
  const districts = (listing as { areas?: { districts?: Array<DistrictShape> } }).areas?.districts;
  const schoolDistrict = districts && districts.length > 0 ? districts[0].fullName : null;
  const lastUpdatedAgo = (listing as { history?: { lastUpdated?: { ago?: string } } }).history?.lastUpdated?.ago;
  const modelsAndPricing = (listing as { modelsAndPricing?: Array<ModelPricingShape> }).modelsAndPricing;

  const formattedUnits =
    modelsAndPricing
      ?.filter((unit) => (unit.quantity ?? 0) > 0)
      .map((unit) => {
        const beds = unit.numBeds ?? 0;
        const bedString = beds === 0 ? 'studio apartment' : `${numberAsWord(beds)}-bedroom apartment`;
        const qty = unit.quantity ?? 0;
        const priceTail = unit.lowPrice ? ` for ${asCurrency(unit.lowPrice)}` : '.';
        const priceTailPlural = unit.lowPrice ? ` starting at ${asCurrency(unit.lowPrice)}` : '.';
        return qty === 1
          ? `There is ${qty} ${bedString} available${priceTail}`
          : `There are ${qty} ${bedString}s available${priceTailPlural}`;
      }) ?? [];

  const questions: Array<FaqQuestion> = [];

  if (!isHouse && formattedUnits.length > 0) {
    questions.push({
      question: 'How many apartment units are available and what are their rents?',
      answerType: 'unorderedList',
      answer: formattedUnits,
    });
  }

  if (buildingName) {
    questions.push({
      question: `What is ${isHouse ? "this home's" : "the apartment building's"} pet policy?`,
      answerType: 'answer',
      answer: petPolicy
        ? `There are ${formattedPet} at ${buildingName}. Please contact the property manager for up to date pet policy and fee information.`
        : `Please contact the property manager at ${buildingName} for up to date pet policy information.`,
    });

    questions.push({
      question: `Is there laundry in the ${isHouse ? 'house' : 'building'}?`,
      answerType: 'answer',
      answer:
        laundry && laundry !== 'none'
          ? `This building offers ${formattedLaundry} laundry.`
          : laundry === 'none'
            ? `No, there is no laundry offered at ${buildingName}.`
            : `Please contact ${buildingName} for information regarding laundry amenities.`,
    });

    questions.push({
      question: `Does the ${isHouse ? 'home' : 'building'} offer parking?`,
      answerType: 'answer',
      answer: parking
        ? `Yes, ${buildingName} provides parking.`
        : `For up to date tenant parking information, please contact ${buildingName}.`,
    });

    if (city && neighborhood) {
      questions.push({
        question: `What ${city} neighborhood is ${buildingName} in?`,
        answerType: 'answer',
        answer: `Your new apartment would be located in the ${neighborhood} neighborhood.`,
      });
    }

    questions.push({
      question: `What school district is this ${isHouse ? 'home' : 'building'} in?`,
      answerType: 'answer',
      answer: schoolDistrict
        ? `${buildingName} is within the ${schoolDistrict}. Check out the additional Nearby Schools for more boundary details.`
        : `Please check with property manager to determine the school district ${buildingName} is in.`,
    });

    if (lastUpdatedAgo) {
      questions.push({
        question: 'When was this listing last updated?',
        answerType: 'answer',
        answer: `The property manager updated this rental listing ${lastUpdatedAgo}.`,
      });
    }
  }

  return questions;
};

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const formatAnswerForSchema = (q: FaqQuestion): string => {
  const type = q.answerType ?? 'answer';
  if (type === 'answer') return q.answer as string;

  const items = Array.isArray(q.answer) ? q.answer : [];
  const ListEl = type === 'unorderedList' ? 'ul' : 'ol';
  const lis = items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const list = `<${ListEl}>${lis}</${ListEl}>`;
  return q.listHeader ? `${q.listHeader}${list}` : list;
};

export const buildFaqJsonLd = (questions: Array<FaqQuestion>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map((q) => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: formatAnswerForSchema(q),
    },
  })),
});
